import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import SalaryInput from "./SalaryInput";
import { calculateSalaryPercentile, formatCurrency, PercentileDistribution } from "@/lib/salaryUtils";

interface PercentileData {
  percentile: number;
  salary: number;
  label: string;
}

interface PercentileChartProps {
  data: PercentileData[];
  occupationMeanSalary?: number;
  userSalary?: number;
  title?: string;
  className?: string;
  allowInput?: boolean;
  onSalaryChange?: (salary: number | null) => void;
  showOccupationBenchmark?: boolean;
}

const PercentileChart = ({ 
  data, 
  occupationMeanSalary,
  userSalary: initialUserSalary,
  title = "Salary Percentiles",
  className = '',
  allowInput = true,
  onSalaryChange,
  showOccupationBenchmark = true
}: PercentileChartProps) => {
  const [userSalary, setUserSalary] = useState<number | null>(initialUserSalary || null);
  
  // Load saved salary from localStorage on mount
  useEffect(() => {
    if (!initialUserSalary && allowInput) {
      const saved = localStorage.getItem('user-salary');
      if (saved) {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed)) {
          setUserSalary(parsed);
        }
      }
    }
  }, [initialUserSalary, allowInput]);

  const handleSalaryChange = (salary: number | null) => {
    setUserSalary(salary);
    
    // Save to localStorage
    if (salary) {
      localStorage.setItem('user-salary', salary.toString());
    } else {
      localStorage.removeItem('user-salary');
    }
    
    // Call parent callback
    onSalaryChange?.(salary);
  };

  // Calculate user's percentile if they have entered a salary
  const userPercentile = userSalary && data.length >= 4 ? (() => {
    const distribution: PercentileDistribution = {
      p10: data.find(d => d.percentile === 10)?.salary || 0,
      p25: data.find(d => d.percentile === 25)?.salary || 0,
      p50: data.find(d => d.percentile === 50)?.salary || 0,
      p75: data.find(d => d.percentile === 75)?.salary || 0,
      p90: data.find(d => d.percentile === 90)?.salary || 0,
    };
    return calculateSalaryPercentile(userSalary, distribution);
  })() : null;

  // Calculate occupation mean percentile for context
  const occupationMeanPercentile = occupationMeanSalary && data.length >= 4 ? (() => {
    const distribution: PercentileDistribution = {
      p10: data.find(d => d.percentile === 10)?.salary || 0,
      p25: data.find(d => d.percentile === 25)?.salary || 0,
      p50: data.find(d => d.percentile === 50)?.salary || 0,
      p75: data.find(d => d.percentile === 75)?.salary || 0,
      p90: data.find(d => d.percentile === 90)?.salary || 0,
    };
    return calculateSalaryPercentile(occupationMeanSalary, distribution);
  })() : null;

  const getPercentileColor = (percentile: number) => {
    if (percentile >= 90) return 'bg-wage-secondary';
    if (percentile >= 75) return 'bg-wage-accent';
    if (percentile >= 50) return 'bg-wage-primary';
    if (percentile >= 25) return 'bg-muted-foreground';
    return 'bg-destructive';
  };

  return (
    <Card className={`shadow-sm ${className}`}>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-foreground">
          {title}
        </CardTitle>
        {showOccupationBenchmark && occupationMeanSalary && occupationMeanPercentile && (
          <div className="text-sm text-muted-foreground mb-2">
            Occupation mean: <span className="font-medium text-wage-secondary">{formatCurrency(occupationMeanSalary)}</span>
            {' '}({Math.round(occupationMeanPercentile)}th percentile)
          </div>
        )}
        {userSalary && userPercentile && (
          <div className="text-sm text-muted-foreground">
            Your salary: <span className="font-medium text-wage-primary">{formatCurrency(userSalary)}</span>
            {' '}({Math.round(userPercentile)}th percentile - {Math.round(userPercentile)}% of workers in this occupation earn less)
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="font-medium text-foreground">{item.label}</span>
              <span className="text-muted-foreground">{formatCurrency(item.salary)}</span>
            </div>
            <div className="relative">
              <Progress 
                value={item.percentile} 
                className="h-2" 
              />
              <div 
                className={`absolute top-0 left-0 h-2 rounded-full ${getPercentileColor(item.percentile)}`}
                style={{ width: `${item.percentile}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground text-right">
              {item.percentile}th percentile
            </div>
          </div>
        ))}
        
        
        {/* Salary Input Section */}
        {allowInput && (
          <div className="pt-4 border-t border-border">
            <SalaryInput
              onSalaryChange={handleSalaryChange}
              currentSalary={userSalary}
              className="mb-4"
            />
          </div>
        )}

        {/* User Position Display */}
        {userSalary && userPercentile && (
          <div className="pt-4 border-t border-border">
            <div className="flex justify-between items-center text-sm font-medium">
              <span className="text-wage-primary">Your Position</span>
              <span className="text-wage-primary">{formatCurrency(userSalary)}</span>
            </div>
            <div className="relative mt-2">
              <Progress value={userPercentile} className="h-3" />
              <div 
                className="absolute top-0 left-0 h-3 rounded-full bg-wage-primary"
                style={{ width: `${userPercentile}%` }}
              />
            </div>
            <div className="text-xs text-wage-primary text-right mt-1 font-medium">
              {Math.round(userPercentile)}th percentile - {Math.round(userPercentile)}% of people in this occupation earn less than you
            </div>
            
            {/* Goal Calculator */}
            {userPercentile < 75 && (
              <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  To reach the 75th percentile, you'd need: <span className="font-medium text-foreground">
                    {formatCurrency(data.find(d => d.percentile === 75)?.salary || 0)}
                  </span>
                  {' '}(+{formatCurrency((data.find(d => d.percentile === 75)?.salary || 0) - userSalary)})
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PercentileChart;